import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import {
  Typography, Checkbox, FormControlLabel, Link,
} from '@material-ui/core';

import { PreOnboardingParams } from '../../PreOnboardingPage';

import { Button, CoverageFields } from '@/components';
import { ENROLL_COVERED_EMPLOYEE_PATIENT } from '@/apollo/mutations';
import { EligibilityFormCustomization } from '@/pages/Onboarding/OnboardingFlowContext';
import { getPdtUid } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { useUtmParams } from '@/hooks/useUtmParams';
import { formatDateString } from '@/utils/stringUtils';
import { logError } from '@/utils/logError';

type Props = {
  formCustomization: EligibilityFormCustomization;
  onExistingUser: () => void;
  onIneligible: () => void;
  onError: () => void;
};

const CoverageCheckWithEmail = ({
  formCustomization, onExistingUser, onIneligible, onError,
}: Props) => {
  const { t } = useTranslation();
  const { slug, product } = useParams<PreOnboardingParams>();
  const history = useHistory();
  const posthog = usePostHog();
  const { utmParams } = useUtmParams();

  useEffect(() => {
    if (posthog) {
      posthog.capture('CoverageCheckStarted', { program: getProgram(product), org_slug: slug });
    }
  }, [posthog, product, slug]);

  const {
    handleSubmit, control, formState: { isValid },
  } = useFormContext();

  const [enrollCoveredEmployeePatient, { loading }] = useMutation(
    ENROLL_COVERED_EMPLOYEE_PATIENT,
  );

  const [submitError, setSubmitError] = useState(false);

  const onSubmit = useCallback(async (data: Record<string, string>) => {
    setSubmitError(false);

    try {
      const response = await enrollCoveredEmployeePatient({
        variables: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfBirth: formatDateString(data.dateOfBirth),
          postalCode: formCustomization.showZipCode ? data.zipCode : undefined,
          employeeId: formCustomization.showEmployeeId ? data.employeeId : undefined,
          organizationSlug: slug,
          pdtUid: getPdtUid(product),
          termsAndPrivacyAcknowledgedAt: new Date().toISOString(),
          posthogDistinctId: posthog.get_distinct_id(),
          ...utmParams,
        },
      });

      const result = response?.data?.enrollCoveredEmployeePatient;

      // Must resolve the person before ActivationFlowSubmitted carries their cortex_id.
      if (result?.eligible || result?.existingUser) {
        posthog.identify(result.distinctId);
      }

      posthog.capture('ActivationFlowSubmitted', {
        program: getProgram(product),
        ...(result?.cortexId ? { 'wellness.cortex_id': result.cortexId } : {}),
      });

      if (!result?.success) {
        onError();
        return;
      }

      if (result.eligible) {
        const params = new URLSearchParams({ skipConfirm: '1' });
        const redirectUri = new URLSearchParams(window.location.search).get('redirect_uri');
        if (redirectUri) {
          params.set('redirect_uri', redirectUri);
        }
        history.push(`/coverage/${product}/${slug}/${result.careTeamUid}/${result.token}?${params.toString()}`);
        return;
      }

      if (result.existingUser) {
        // Confirms this outcome specifically, unlike the mount-time CoverageCheckStarted.
        posthog.capture('ActivationFlowEnteredCheckYourEmail', {
          program: getProgram(product),
          'wellness.cortex_id': result.cortexId,
        });
        onExistingUser();
        return;
      }

      onIneligible();
    } catch (e) {
      logError('[CoverageCheckWithEmail]', 'enrollment exception', e);
      setSubmitError(true);
    }
  }, [enrollCoveredEmployeePatient, formCustomization, slug, product, posthog, utmParams]);

  return (
    <>
      <Typography variant="h2" style={{ marginTop: 32, marginBottom: 8 }} color="textPrimary">
        {t('coverage.sleepio.patient.coverage_check_with_email.title')}
      </Typography>
      <Typography style={{ marginBottom: 32 }} color="textPrimary">
        {t('coverage.sleepio.patient.confirm_details.subtitle')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CoverageFields formCustomization={formCustomization} showEmail />
        <Controller
          name="termsAndPrivacy"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControlLabel
              style={{ marginTop: 24, marginBottom: 8, alignItems: 'flex-start' }}
              label={(
                <Typography>
                  {`${t('coverage.i_agree_to_the')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.terms_of_use.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {t('footer.terms_of_use.label')}
                  </Link>
                  {` ${t('coverage.and')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.privacy_notice.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {`${t('footer.privacy_notice.label')}.`}
                  </Link>
                </Typography>
              )}
              control={<Checkbox {...field} />}
            />
          )}
        />
        {submitError && (
          <Typography color="error" style={{ marginTop: 8 }}>
            {t('coverage.errors.generic_error')}
          </Typography>
        )}
        <Button
          role="button"
          variant="contained"
          data-testid="submit-button"
          type="submit"
          fullWidth
          color="primary"
          disabled={!isValid}
          rounded
          loading={loading}
          style={{ marginTop: 24 }}
        >
          {t('coverage.sleepio.patient.confirm_details.submit_button')}
        </Button>
      </form>
    </>
  );
};

export default CoverageCheckWithEmail;
